import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { apiClient } from '../../lib/api-client';
import { formatDate } from '../../utils/date';
import { Mail, Check, Reply, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
  status: 'new' | 'sent' | 'failed' | 'read' | 'replied';
  created_at: string;
  updated_at: string;
}

const ContactMessageManagement: React.FC = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      console.log('📧 Fetching contact messages...');
      const data = await apiClient.getContactMessages();
      console.log('📧 Contact messages received:', data);

      // Handle both array response and object with messages property
      const messagesList = Array.isArray(data) ? data : (data?.messages || []);
      setMessages(messagesList);

      if (messagesList.length === 0) {
        console.log('📧 No contact messages found in database');
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Failed to load contact messages';
      toast.error(errorMsg);
      console.error('❌ Error fetching contact messages:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        status: error?.status,
        data: error?.data
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string, status: 'read' | 'replied') => {
    try {
      setIsUpdating(true);
      await apiClient.updateContactMessageStatus(id, status);
      setMessages(messages.map(msg =>
        msg._id === id ? { ...msg, status, updated_at: new Date().toISOString() } : msg
      ));
      if (selectedMessage && selectedMessage._id === id) {
        setSelectedMessage({ ...selectedMessage, status, updated_at: new Date().toISOString() });
      }
      toast.success(`Message marked as ${status}`);
    } catch (error) {
      toast.error('Failed to update message status');
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setIsUpdating(true);
      await apiClient.deleteContactMessage(id);
      setMessages(messages.filter(msg => msg._id !== id));
      if (selectedMessage && selectedMessage._id === id) {
        setSelectedMessage(null);
      }
      toast.success('Message deleted successfully');
    } catch (error) {
      toast.error('Failed to delete message');
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="default" className="bg-blue-600 text-white">New</Badge>;
      case 'sent':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'read':
        return <Badge variant="outline" className="bg-purple-100 text-purple-800">Read</Badge>;
      case 'replied':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800">Replied</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-900">Contact Messages Management</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">Manage all messages received through the Contact Us form.</p>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No contact messages found.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Messages List */}
              <div className="lg:col-span-2">
                <div className="border rounded-lg overflow-hidden shadow-sm">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {messages.map((message) => (
                        <TableRow
                          key={message._id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => setSelectedMessage(message)}
                        >
                          <TableCell>{formatDate(message.created_at)}</TableCell>
                          <TableCell className="font-medium">{message.name}</TableCell>
                          <TableCell>{message.subject}</TableCell>
                          <TableCell>{getStatusBadge(message.status)}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMessage(message);
                                }}
                                className=""
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {message.status !== 'read' && message.status !== 'replied' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusUpdate(message._id, 'read');
                                  }}
                                  className=""
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              {message.status !== 'replied' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusUpdate(message._id, 'replied');
                                  }}
                                  className=""
                                >
                                  <Reply className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(message._id);
                                }}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Message Details */}
              <div className="lg:col-span-1">
                {selectedMessage ? (
                  <Card className="shadow-md border-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xl">Message Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="font-medium">{formatDate(selectedMessage.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">From</p>
                          <p className="font-medium">{selectedMessage.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="font-medium text-blue-600 hover:underline cursor-pointer">
                            <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Subject</p>
                          <p className="font-medium">{selectedMessage.subject}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Category</p>
                          <p className="font-medium">{selectedMessage.category || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Message</p>
                          <p className="font-medium bg-gray-50 p-3 rounded border border-gray-200 whitespace-pre-wrap">{selectedMessage.message}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <p className="font-medium">{getStatusBadge(selectedMessage.status)}</p>
                        </div>
                        <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-2">
                          {selectedMessage.status !== 'read' && selectedMessage.status !== 'replied' && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isUpdating}
                              onClick={() => handleStatusUpdate(selectedMessage._id, 'read')}
                              className="w-full sm:w-auto"
                            >
                              <Check className="w-4 h-4 mr-1" /> Mark as Read
                            </Button>
                          )}
                          {selectedMessage.status !== 'replied' && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isUpdating}
                              onClick={() => handleStatusUpdate(selectedMessage._id, 'replied')}
                              className="w-full sm:w-auto"
                            >
                              <Reply className="w-4 h-4 mr-1" /> Mark as Replied
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isUpdating}
                            onClick={() => handleDelete(selectedMessage._id)}
                            className="w-full sm:w-auto text-red-600 border-red-300 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="text-center py-8 text-gray-500">Select a message to view details</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactMessageManagement;
